import { message, Modal, Spin } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import cryptoRandomString from 'crypto-random-string';
import { random } from 'lodash';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import FormContent from './FormContent';
import { TStore } from '@/common/basis/types/tstore';
import { TBookingMeeting } from '@/common/basis/types/database/tbooking-meeting';
import { setBookingRoomId, setIsHandBookingSuccess, setIsOpenBookingMeeting } from '@/common/store/booking/slice';
import { TField } from '@/common/basis/types/tfield';
import { createBookingMeeting, getBookingDetail, modifyBookingMeeting } from '@/common/http/meet';
import { isSuccessCode } from '@/common/utils/response';
import { decrypt, encrypt } from '@/common/utils/crypto-js';
import { TCryptoJS } from '@/common/basis/types/tcrypto-js';
import { useMeetingBookingType } from '@/common/hooks/meeting-booking-type';

function App() {
  const dispatch = useDispatch();
  const isOpenBookingMeeting = useSelector((state: TStore.State) => state.booking.isOpenBookingMeeting);
  const currentUserName = useSelector((state: TStore.State) => state.person.user?.userName);
  const bookingRoomId = useSelector((state: TStore.State) => state.booking.bookingRoomId);
  const bookingMeetingType = useSelector((state: TStore.State) => state.booking.bookingMeetingType);
  const { isBookingLive } = useMeetingBookingType();
  const isCreateBooking = useMemo(() => !bookingRoomId, [bookingRoomId]); // 是否是否新建预约
  const now = dayjs();
  const [isOkLoading, setIsOkLoading] = useState(false);
  const [isBookingLoading, setIsBookingLoading] = useState(false);
  const [formData, setFormData] = useState<TBookingMeeting.Data | null>(null);
  const [messageApi, contextHolder] = message.useMessage({ top: 60 });
  const [isEableConfirm, setIsEableConfirm] = useState(false);
  const { t } = useTranslation();
  const title = bookingRoomId
    ? isBookingLive
      ? t('editLiveStreaming')
      : t('editBookingMeeting')
    : isBookingLive
      ? t('liveStreaming')
      : t('bookingMeeting');

  // 新建或编辑预约会议
  const requestBookingMeeting = async () => {
    if (isOkLoading) {
      return;
    }
    const roomName = formData?.roomName;

    if (!roomName?.replace(/ /, '')) {
      messageApi.destroy();
      messageApi.error(t('enterMeetingTheme'));

      return;
    }
    const password = formData?.password ?? '';
    const nonce = formData?.nonce ?? '';
    const pwd = decrypt({ str: password, key: nonce, iv: TCryptoJS.MEET_CLIENT_IV });

    if (pwd.length !== 6) {
      messageApi.destroy();
      messageApi.error(t('passwordFormat'));

      return;
    }

    setIsOkLoading(true);
    try {
      const exceedNum = formData?.autoMute === TField.MEET_AUTO_MUTE_EXCEED ? 6 : 0;
      const resp = isCreateBooking
        ? await createBookingMeeting({ ...formData, exceedNum, meetingType: bookingMeetingType })
        : await modifyBookingMeeting({ ...formData, exceedNum, meetingType: bookingMeetingType });

      if (isSuccessCode(resp?.code)) {
        messageApi.destroy();
        messageApi.success(t('bookingSuccessful'));
        dispatch(setIsOpenBookingMeeting(false));
        dispatch(setIsHandBookingSuccess(true)); // 操作成功 刷新`我的会议`列表
      } else {
        messageApi.destroy();

        if (isCreateBooking) {
          messageApi.error(resp?.msg);
        } else {
          messageApi.error(t('bookingFail'));
        }
      }
    } catch (err) {
      // TODO
    }
    setIsOkLoading(false);
  };

  // 获取需要编辑预约的详情数据
  const requestBookingDetail = async () => {
    if (isBookingLoading || !bookingRoomId) {
      return;
    }

    setIsBookingLoading(true);
    const resp = await getBookingDetail({ roomId: bookingRoomId });

    if (isSuccessCode(resp?.code)) {
      setFormData(resp.data);
    }
    setIsBookingLoading(false);
  };

  useEffect(() => {
    setIsEableConfirm(!!formData?.roomName?.replace(/ /, ''));
  }, [formData?.roomName]);

  useEffect(() => {
    if (isOpenBookingMeeting) {
      messageApi.destroy(); // 清空提示信息
      if (isCreateBooking) {
        const nonce = cryptoRandomString({ length: 16 });
        const password = encrypt({ str: `${random(100000, 999999)}`, key: nonce, iv: TCryptoJS.MEET_CLIENT_IV });

        setFormData({
          ...formData,
          nonce,
          roomName: isBookingLive
            ? t('initiateMeetingLive', { userName: currentUserName })
            : t('initiateMeeting', { userName: currentUserName }),
          password,
          startTime: now.set('minute', now.get('minute') < 30 ? 30 : 60).valueOf(),
          repeatType: TField.MEET_REPEAT_TYPE_NONE,
          autoMute: TField.MEET_AUTO_MUTE_EXCEED,
          duration: 60 * 60 * 1000,
          allowAnonymous: true,
        });
      } else {
        requestBookingDetail(); // 获取预约详情
      }
    } else {
      setFormData(null); // 清空表单内容
      dispatch(setBookingRoomId('')); // 清空roomId
    }
  }, [isOpenBookingMeeting, isCreateBooking]);

  return (
    <>
      <Modal
        cancelText={t('cancel')}
        forceRender
        keyboard={false}
        maskClosable={false}
        okButtonProps={{ loading: isOkLoading, disabled: !isEableConfirm }}
        okText={t('bookNow')}
        onCancel={() => dispatch(setIsOpenBookingMeeting(false))}
        onOk={() => requestBookingMeeting()}
        open={isOpenBookingMeeting}
        title={
          <div className='text-[16px] font-medium'>
            <span className='text-[18px]'>{title}</span>
          </div>
        }
        width={542}
      >
        {isBookingLoading ? (
          <div className='flex min-h-[280px] justify-center py-10'>
            <Spin />
          </div>
        ) : formData ? (
          <FormContent formData={formData} setFormData={setFormData} />
        ) : (
          <></>
        )}
      </Modal>
      {contextHolder}
    </>
  );
}

export default App;
